import React from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash.isequal';
import { ReadingInstructionMappers } from '@amplify/instruction-groups';
import get from 'lodash.get';
import head from 'lodash.head';
import { getUrlParams } from '../../../utils/shared/urlUtils';
import Instruction from '../../../features/instruction/shared/Instruction';
import BulkActivitiesLink from '../../../features/instruction/shared/bulkActivitiesLink/BulkActivitiesLink';
import {
    switchInstructionDate,
    switchInstructionGrade,
    switchInstructionTab,
    viewLatestUpdate,
} from '../../../api/instruction/shared/instructionActions';
import {
    saveCheckPoint,
    processCheckPoints,
    fetchObservations,
} from '../../../api/instruction/reading/instructionActions';
import {
    demoProcessCheckPoints,
    demoFetchObservations,
    demoSaveCheckPoint,
} from '../../../api/demoMode/reading/demoModeActions';
import {
    getGroupsHistoricalMessage,
    getGroupsMessage,
} from '../../../constants/shared/InstructionConstants';
import { selectors } from '../../../root/reading/root';
import LinkWrapper from '../../../features/navigation/LinkWrapper';
import InterventionGroupLink from '../../../features/intervention/reading/InterventionGroupLink';
import Students from '../../../features/instruction/shared/students/Students';
import { ALL_MEASURES } from '../../../constants/reading/MeasuresConstants';
import CKLABlock from '../../../features/instruction/reading/cklaBlock/cklaBlock';
import { LICENSE } from '../../../constants/reading/CommonConstants';
import D8InstructionClient from '../../../api/clients/reading/InstructionClient';
import { actionStarted, actionSuccessfullyFinished } from '../../../api/actionCreators';
import { FETCH_FAKE_CHECKPOINTS } from '../../../api/actionTypes/shared/apiActionTypes';
import CommonCellBuilder from '../../../components/cell/reading/CommonCellBuilder';
import { CELL_TYPE } from '../../../constants/shared/CellConstants';
import { isMeasureEnabledForSpecificMapper } from '../../../utils/shared/tableUtils';
import {
    downloadAllActivities,
    helpIconClick,
    observationSelect,
    seeActivitiesToggle,
    selectSkill,
    selectIndividualStudent,
    selectPage,
    downloadActivity,
    switchInstructionTabLrs as lrsSwitchInstructionTab,
    lrsLanguageConsiderationPdfDownload,
    switchLanguageConsiderationsTabLrs,
} from '../../../api/lrs/shared/lrsActions';
import KeycloakClient from '../../../api/clients/shared/KeycloakClient';
import { STUDENT } from '../../../constants/shared/CommonConstants';
import { formLrsGroupName } from '../../../features/lrs/shared/lrsHelpers';

const { READING_ACTIVITIES_SKILLS, READING_ACTIVITIES_MAPPER, OBSERVATION_ACTIVITIES_MAPPER } =
    ReadingInstructionMappers;
const ACTIVITIES_MESSAGE = `All available activities for student small group and individual instruction are displayed by skill area; materials for educators are in Teacher Resources. Select a tab at the left to view the associated activities or resources below.`;

const mapStateToProps = state => ({
    location: selectors.getLocation(state),
    lastLocation: selectors.getLastLocation(state),
    dates: selectors.getSelectedGradeCheckPointObject(state).dates,
    selectedDate: selectors.getSelectedInstructionDate(state),
    areCheckPointsFetched: selectors.areCheckPointsFetched(state),
    areObservationsFetching: selectors.areObservationsFetching(state),
    isCheckPointSaving: selectors.isCheckPointSaving(state),
    selectedGrade: selectors.getSelectedInstructionGrade(state),
    currentPeriod: selectors.getCurrentPeriod(state),
    checkPointPeriod: selectors.getCheckPointPeriod(state),
    grades: selectors.getGradesInCurrentClass(state),
    groups: selectors.getInstructionGroupsForSelectedGrade(state),
    observationsContent: selectors.getObservations(state),
    activitiesSkills: Object.values(READING_ACTIVITIES_SKILLS),
    activitiesMapper: READING_ACTIVITIES_MAPPER,
    activitiesMessage: ACTIVITIES_MESSAGE,
    studentColumns: selectors.getInstructionStudentsColumns(state),
    studentsData: selectors.getInstructionStudentsDataForSelectedGrade(state),
    LinkWrapper,
    GroupWidget: InterventionGroupLink,
    staffId: selectors.getUserId(state),
    lrsTrackingInfo: selectors.getLrsTrackingInfo(state),
    optionalParams: { burstGroupUid: getUrlParams(state.router.location).burstGroupUid },
    measureList: ALL_MEASURES,
    observationActivitiesMapper: OBSERVATION_ACTIVITIES_MAPPER,
    Widget: selectors.hasAppLicenseByName(state, LICENSE.CKLA_2_EDITION) ? CKLABlock : <></>,
    BulkActivitiesWidget: BulkActivitiesLink,
    gradeToMeasureMapper: selectors.getGradeToMeasureMapper(state),
    licensingInfo: selectors.getLicensingInfo(state),
    isDemoModeOn: selectors.getIsDemoModeOn(state),
    sessionId: KeycloakClient.getSessionId(),
    demoClass: selectors.getDemoClass(state),
    isGuidedTourOn: selectors.getIsGuidedTourOn(state),
    isSecure: selectors.isSecureResultFound(state),
});

const mapDispatchToProps = dispatch => ({
    fetchObservations: isDemoModeOn =>
        isDemoModeOn
            ? () => dispatch(demoFetchObservations())
            : () => dispatch(fetchObservations()),
    lrsLanguageConsiderationPdf: ({ activityId, title }) =>
        dispatch(lrsLanguageConsiderationPdfDownload({ activityId, title })),
    switchLanguageConsiderationsTabLrs: (tab, group) =>
        dispatch(switchLanguageConsiderationsTabLrs(tab, group)),
    processCheckPoints: (isDemoModeOn, demoClass) =>
        isDemoModeOn
            ? () => dispatch(demoProcessCheckPoints(demoClass))
            : () => dispatch(processCheckPoints(D8InstructionClient)),
    saveCheckPoint: isDemoModeOn =>
        isDemoModeOn
            ? () => dispatch(demoSaveCheckPoint())
            : () => dispatch(saveCheckPoint(D8InstructionClient)),
    switchInstructionTab: tab => {
        dispatch(switchInstructionTab(tab));
    },
    switchInstructionDate: ({ date }, prevDate) => {
        dispatch(switchInstructionDate({ date }, prevDate));
    },
    viewLatestUpdateRecommendation: ({ date, buttonText }) => {
        dispatch(viewLatestUpdate({ date, buttonText }));
    },
    switchInstructionGrade: grade => {
        dispatch(switchInstructionGrade(grade));
    },
    navigateToLatestDateStart: () => dispatch(actionStarted(FETCH_FAKE_CHECKPOINTS.START)),
    navigateToLatestDateDone: () =>
        dispatch(actionSuccessfullyFinished(FETCH_FAKE_CHECKPOINTS.SUCCESS)),
    selectStudent: (id, group) => dispatch(selectIndividualStudent(id, group)),
    onHelpIconClick: (name, category, assessment, studentId, group) =>
        dispatch(helpIconClick(name, category, assessment, studentId, group)),
    seeActivitiesToggle: (collapsed, group) => {
        dispatch(seeActivitiesToggle(collapsed, group));
    },
    selectIndividualStudent: id => {
        dispatch(selectIndividualStudent(id));
    },
    downloadAllActivities: (id, activities, group) => {
        dispatch(downloadAllActivities(id, activities, group));
    },
    selectObservation: (checked, name, category, assessment, studentId, group) => {
        dispatch(observationSelect(checked, name, category, assessment, studentId, group));
    },
    selectPage: (page, activityIds, pagesList, group) => {
        dispatch(selectPage(page, activityIds, pagesList, group));
    },
    selectSkill: (skill, grade) => {
        dispatch(selectSkill(skill, grade));
    },
    downloadActivity: (activity, grade, activityId) => {
        dispatch(downloadActivity(activity, grade, activityId));
    },
    switchInstructionTabLrs: (tabName, group) => {
        dispatch(lrsSwitchInstructionTab(tabName, group));
    },
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const {
        studentsData,
        groups,
        currentPeriod,
        selectedGrade,
        observationsContent,
        studentColumns,
        GroupWidget,
        measureList,
        staffId,
        lrsTrackingInfo,
        gradeToMeasureMapper,
        licensingInfo,
        location,
        lastLocation,
        isDemoModeOn,
        sessionId,
        demoClass,
        isSecure,
    } = stateProps;
    const { onHelpIconClick, selectStudent, selectObservation, switchInstructionTabLrs } =
        dispatchProps;
    const defaultGroup = formLrsGroupName(get(head(studentsData), STUDENT.GROUP_NAME));

    return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        fetchObservations: dispatchProps.fetchObservations(isDemoModeOn),
        processCheckPoints: dispatchProps.processCheckPoints(isDemoModeOn, demoClass),
        saveCheckPoint: dispatchProps.saveCheckPoint(isDemoModeOn),
        isSecure,
        studentsComponent: (
            <Students
                studentsData={studentsData}
                groups={groups}
                selectedGrade={selectedGrade}
                observationsContent={observationsContent}
                columns={studentColumns}
                GroupWidget={GroupWidget}
                measureList={measureList}
                observationActivitiesMapper={OBSERVATION_ACTIVITIES_MAPPER}
                staffId={staffId}
                lrsTrackingInfo={lrsTrackingInfo}
                location={location}
                lastLocation={lastLocation}
                selectIndividualStudent={selectStudent}
                onHelpIconClick={onHelpIconClick}
                selectObservation={selectObservation}
                sessionId={sessionId}
                isSecure={isSecure}
                commonCellBuilderComponent={(column, bmStatuses) => (
                    <CommonCellBuilder
                        cellType={CELL_TYPE.BENCHMARK}
                        gatingMeasureCellType={CELL_TYPE.BENCHMARK_GATING_MEASURE}
                        statusForMeasureYearPeriod={bmStatuses.find(
                            status => status.measure === column,
                        )}
                        measure={column}
                        period={currentPeriod}
                        grade={selectedGrade}
                        optionalCellData={{
                            classNames: `${column.toLowerCase()}-cell clear-cell-styles without-hover`,
                        }}
                        isCellDisabled={
                            !isMeasureEnabledForSpecificMapper(
                                column,
                                selectedGrade,
                                currentPeriod,
                                gradeToMeasureMapper,
                            )
                        }
                    />
                )}
            />
        ),
        isUpdateRecommendationButtonEnabled: isEqual(stateProps.dates[0], stateProps.selectedDate),
        groupsMessage: isEqual(stateProps.dates[0], stateProps.selectedDate)
            ? getGroupsMessage(
                  licensingInfo.includes(LICENSE.D8) || licensingInfo.includes(LICENSE.EXPRESS),
                  licensingInfo.includes(LICENSE.D8) || licensingInfo.includes(LICENSE.EXPRESS),
                  isSecure,
              )
            : getGroupsHistoricalMessage(
                  licensingInfo.includes(LICENSE.D8) || licensingInfo.includes(LICENSE.EXPRESS),
                  isSecure,
              ),
        switchInstructionTabLrs: tab => switchInstructionTabLrs(tab, defaultGroup),
    };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(Instruction);
